/**
 * features/parent/academicHold.tsx
 * ------------------------------------------------------------------
 * Phase 3 — fee-gating of parent academic access.
 *
 * PER PUPIL, NOT PER FAMILY (Aug 2026). LKC does not administer anything at
 * family level, and 694 of 808 live invoices carry no familyId at all — the
 * Pastel opening balances arrived with a studentId and nothing else. Reading
 * arrears `where familyId == ...` therefore saw almost none of the debt:
 * 227 invoices owing P5.58m resolved to TWO families and ONE held pupil out
 * of 826. The gate was on, correctly written, and blind.
 *
 * So the hold is now decided per CHILD, from that child's own invoices, and
 * the override lives at financeAcademicHolds/{studentId}. A parent with two
 * children can see one child's reports and not the other's, which is what
 * per-child arrears means.
 *
 * useParentAcademicHold() returns `heldFor(studentId)`. There is no
 * whole-parent answer any more, because a parent is not the unit being
 * held — deliberately, so a caller cannot ask the vague question.
 *
 * ParentHoldNotice: the "paused" panel shown in place of academic content
 * while a CHILD is held. Messaging is intentionally NOT gated — a held
 * parent may need it to contact the bursary and resolve.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { Lock, Wallet } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { GradientHero, SoftCard, AccentButton } from '@/features/finance/ui';
import {
  studentHasArrears,
  effectiveAcademicHold,
  resolveHoldMode,
  holdDocIdForStudent,
  GLOBAL_HOLD_ID,
  type Invoice,
  type AcademicHoldMode,
} from '@/lib/financeCore';

export function useParentAcademicHold(): {
  /** True when THIS child's academic content is paused. */
  heldFor: (studentId: string) => boolean;
  /** True once invoices AND every override listener have reported. Gate the
   *  UI on this: rendering "paused" before the data lands accuses a parent
   *  who has paid. */
  ready: boolean;
} {
  const { userData } = useAuth();
  const studentIds = useMemo(
    () => userData?.parentAccount?.studentIds ?? [],
    [userData],
  );
  const idKey = studentIds.join(',');

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [modes, setModes] = useState<Record<string, AcademicHoldMode>>({});
  const [globalMode, setGlobalMode] = useState<AcademicHoldMode>('auto');
  const [invReady, setInvReady] = useState(false);
  const [holdsReady, setHoldsReady] = useState(false);
  const [globalReady, setGlobalReady] = useState(false);

  /* Invoices for this parent's children. Already studentId-scoped — it is
     only the aggregation that used to be family-wide. */
  useEffect(() => {
    if (studentIds.length === 0) {
      setInvoices([]);
      setInvReady(true);
      return;
    }
    const ids = studentIds.slice(0, 30);
    const q = query(collection(db, 'financeInvoices'), where('studentId', 'in', ids));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Invoice));
        setInvReady(true);
      },
      () => setInvReady(true),
    );
    return () => unsub();
  }, [idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* One override listener per child. A parent has a handful of children, and
     a doc listener each is both cheaper and easier to authorise than a
     collection query — rules can check "is this my child" on the doc id. */
  useEffect(() => {
    if (studentIds.length === 0) {
      setModes({});
      setHoldsReady(true);
      return;
    }
    setHoldsReady(false);
    const seen = new Set<string>();
    const unsubs = studentIds.map((sid) =>
      onSnapshot(
        doc(db, 'financeAcademicHolds', holdDocIdForStudent(sid)),
        (snap) => {
          setModes((prev) => ({
            ...prev,
            [sid]: snap.exists()
              ? ((snap.data() as { mode?: AcademicHoldMode }).mode ?? 'auto')
              : 'auto',
          }));
          seen.add(sid);
          if (seen.size >= studentIds.length) setHoldsReady(true);
        },
        () => {
          // A denied or failed read must not hold the portal shut. Treat it
          // as no override and fall back to the arrears computation.
          setModes((prev) => ({ ...prev, [sid]: 'auto' }));
          seen.add(sid);
          if (seen.size >= studentIds.length) setHoldsReady(true);
        },
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // School-wide default (readable by any signed-in user per rules).
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'financeAcademicHolds', GLOBAL_HOLD_ID),
      (snap) => {
        setGlobalMode(snap.exists() ? ((snap.data() as { mode?: AcademicHoldMode }).mode ?? 'auto') : 'auto');
        setGlobalReady(true);
      },
      () => setGlobalReady(true),
    );
    return () => unsub();
  }, []);

  const heldFor = useMemo(() => {
    const cache = new Map<string, boolean>();
    return (studentId: string): boolean => {
      if (!studentId) return false;
      const hit = cache.get(studentId);
      if (hit !== undefined) return hit;
      const arrears = studentHasArrears(invoices, studentId);
      const held = effectiveAcademicHold(
        arrears,
        resolveHoldMode(modes[studentId] ?? 'auto', globalMode),
      );
      cache.set(studentId, held);
      return held;
    };
  }, [invoices, modes, globalMode]);

  return { heldFor, ready: invReady && holdsReady && globalReady };
}

export function ParentHoldNotice({ onGoToFees }: { onGoToFees?: () => void }) {
  return (
    <div className="space-y-5">
      <GradientHero icon={Lock} title="Academic access paused" subtitle="There are outstanding fees on your account" />
      <SoftCard>
        <div className="flex items-start gap-3">
          <Wallet size={20} className="text-[var(--accent-link,#0d9488)] mt-0.5 shrink-0" />
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <p className="font-medium text-gray-800 dark:text-gray-100">
              Reports, marks and attendance are temporarily unavailable.
            </p>
            <p>
              Your account has fees that are past their due date. Access to academic
              information is paused until the balance is settled. You can review the
              amount due in the Fees section, or contact the school bursary to make
              arrangements.
            </p>
            {onGoToFees && (
              <div className="pt-1">
                <AccentButton onClick={onGoToFees}>Go to Fees</AccentButton>
              </div>
            )}
            {!onGoToFees && (
              <p className="text-xs text-gray-500">Open <span className="font-medium">Fees</span> from the menu to see the balance.</p>
            )}
          </div>
        </div>
      </SoftCard>
    </div>
  );
}
